import { el, mount, onTap } from './dom.ts';
import { iconEl, type IconName } from './icons.ts';
import { currentPath, navigate, setPageMeta } from '../router.ts';
import { state, subscribe } from '../state.ts';
import { api } from '../api.ts';
import { isCar } from './surface.ts';
import { BRAND, brandMark } from './brand.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { spaceKey } from './space-key.ts';

// Uygulama kabuğu (tasarım v2):
//  - Masaüstünde 84 px ÜST navigasyon. Geniş sürekli sol menü yok.
//  - Seçili sekme dolu hap değil; açık metin + kısa alt çizgi.
//  - Paketler ve Ayarlar hesap menüsünde.
//  - Telefonda dört öğeli alt gezinme; arama üstte kalır.
//  - Kota köşede dev kart değil, hesabın yanında küçük gösterge.

export interface NavItem {
  key: string;
  labelKey: MessageKey;
  path: string;
  icon: IconName;
  requiresIptv?: boolean;
}

export const NAV: NavItem[] = [
  { key: 'kesfet', labelKey: 'nav.discover', path: '/browse', icon: 'compass' },
  { key: 'canli', labelKey: 'nav.live', path: '/live', icon: 'broadcast' },
  { key: 'listem', labelKey: 'nav.mylist', path: '/library', icon: 'list' },
  { key: 'iptv', labelKey: 'nav.iptv', path: '/iptv', icon: 'antenna', requiresIptv: true },
];

// Telefon alt çubuğu. IPTV BURADA OLMALI: Plus'ın açtığı asıl bölüme telefondan
// başka giden yol yok — üst sekmeler dar ekranda gizli. Yetkisi olmayan hesapta
// kilitle gösterilir, kaldırılmaz.
const MOBILE_TABS: NavItem[] = [
  { key: 'kesfet', labelKey: 'nav.discover', path: '/browse', icon: 'compass' },
  { key: 'canli', labelKey: 'nav.live', path: '/live', icon: 'broadcast' },
  { key: 'iptv', labelKey: 'nav.iptv', path: '/iptv', icon: 'antenna', requiresIptv: true },
  { key: 'listem', labelKey: 'nav.mylist', path: '/library', icon: 'list' },
  { key: 'hesap', labelKey: 'nav.account', path: '/settings', icon: 'user' },
];

let mainEl: HTMLElement | null = null;
let searchInput: HTMLInputElement | null = null;
let unsubscribe: (() => void) | null = null;

export function appMain(): HTMLElement {
  if (!mainEl) throw new Error('Uygulama kabuğu kurulmadı');
  return mainEl;
}

/** Landing/giriş gibi açık zeminli ekranlar kabuğu kullanmaz. */
export function leaveShell(): void {
  mainEl = null;
  searchInput = null;
  if (unsubscribe) { unsubscribe(); unsubscribe = null; }
  closeAccountMenu();
}

export function ensureAppShell(): HTMLElement {
  const app = document.getElementById('app')!;
  document.body.classList.remove('on-paper');
  // Oturum arkasındaki ekranlar dizine girmez.
  setPageMeta({ title: BRAND.title, index: false });
  if (mainEl && app.querySelector('.app')) {
    refreshChrome();
    return mainEl;
  }

  mainEl = el('main', { class: 'app-main', id: 'app-main' });
  const nav = el('div', { class: 'app-nav', id: 'app-nav' });
  const navbar = el('nav', { class: 'app-navbar', 'aria-label': t('nav.mainNav') }, nav);
  const tabbar = el('nav', { class: 'tabbar', id: 'tabbar', 'aria-label': t('nav.sections') });

  mount(app, el('div', { class: 'app' }, navbar, el('div', { class: 'app-body' }, mainEl), tabbar));
  refreshChrome();
  if (!unsubscribe) unsubscribe = subscribe(refreshChrome);
  return mainEl;
}

function isActive(path: string, item: { path: string; key: string }): boolean {
  if (item.key === 'kesfet') return path === '/browse' || path === '/kesfet' || path === '/';
  if (item.key === 'iptv') return path.startsWith('/iptv') || path.startsWith('/series/');
  if (item.key === 'hesap') return path === '/settings' || path === '/plans' || path.startsWith('/checkout');
  if (item.key === 'listem') return path === '/library';
  return path === item.path;
}

function refreshChrome(): void {
  const nav = document.getElementById('app-nav');
  const tabbar = document.getElementById('tabbar');
  if (!nav || !tabbar) return;

  const path = currentPath();
  const session = state.session;
  const hasIptv = !!session?.entitlements.iptv;

  const wordmark = el('button', { class: 'wordmark', type: 'button', 'aria-label': t('nav.home') },
    brandMark(24), el('span', {}, BRAND.name));
  onTap(wordmark, () => navigate('/browse'));

  const car = isCar();
  const tabs = el('div', { class: 'tabs' },
    ...NAV.map((item) => {
      const label = t(item.labelKey);
      const button = el('button', {
        class: 'nav-tab' + (isActive(path, item) ? ' active' : ''),
        type: 'button',
        'aria-current': isActive(path, item) ? 'page' : undefined,
      }, car ? el('span', { class: 'tab-ico' }, iconEl(item.icon)) : null, el('span', {}, label));
      if (item.requiresIptv && !hasIptv) {
        const lock = el('span', { class: 'lock', title: t('nav.plusOnly') });
        lock.appendChild(iconEl('lock'));
        button.appendChild(lock);
      }
      onTap(button, () => navigate(item.path));
      return button;
    }),
  );

  searchInput = el('input', {
    type: 'search',
    placeholder: t('nav.searchPlaceholder'),
    'aria-label': t('nav.search'),
    autocomplete: 'off',
    enterkeyhint: 'search',
  });
  searchInput.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key !== 'Enter') return;
    const value = searchInput!.value.trim();
    if (value) navigate(`/search?q=${encodeURIComponent(value)}`);
  });
  let search: HTMLElement = el('div', { class: 'nav-search' }, iconEl('search'), searchInput);
  if (car) {
    // Araçta dar alt-çizgi alanına isabet ettirmek zor; arama kendi ekranında.
    const open = el('button', { class: 'nav-search-btn', type: 'button', 'aria-label': t('nav.search') },
      iconEl('search'), el('span', {}, t('nav.search')));
    onTap(open, () => navigate('/search'));
    search = open;
  }

  const quota = session?.quota;
  const quotaTag = quota && quota.limitSeconds !== null ? quotaIndicator(quota.remainingSeconds ?? 0, quota.limitSeconds) : null;

  const planTag = session && session.entitlements.effectivePlan !== 'free'
    ? el('span', { class: 'plan-tag' }, session.entitlements.planName)
    : null;

  // Avatarın bir menü açtığı hiçbir yerde belli değildi: Paketler ve Ayarlar'a
  // giden tek yol buydu. Yanına küçük bir ok koyuyoruz.
  const avatar = el('button', {
    class: 'avatar', type: 'button', 'aria-label': t('nav.accountMenu'),
    'aria-haspopup': 'menu', 'aria-expanded': 'false', title: t('nav.accountMenuTitle'),
  },
    el('span', { class: 'avatar-initial' }, initialOf(session?.user.displayName || session?.user.email || 'A')),
    el('span', { class: 'avatar-caret' }, iconEl('chevronDown')),
  );
  onTap(avatar, (event) => { (event as Event).stopPropagation?.(); toggleAccountMenu(); });

  mount(nav, wordmark, tabs, el('span', { class: 'spacer' }), search, quotaTag, planTag, avatar);

  mount(tabbar, ...MOBILE_TABS.map((item) => {
    const label = t(item.labelKey);
    const locked = !!item.requiresIptv && !hasIptv;
    const glyph = el('span', { class: 'tab-ico' }, iconEl(item.icon));
    if (locked) glyph.appendChild(el('span', { class: 'tab-lock' }, iconEl('lock')));
    const button = el('button', {
      type: 'button',
      class: (isActive(path, item) ? 'active' : '') + (locked ? ' locked' : ''),
      'aria-current': isActive(path, item) ? 'page' : undefined,
      'aria-label': locked ? t('nav.lockedLabel', { label }) : undefined,
    }, glyph, el('span', {}, label));
    onTap(button, () => navigate(item.path));
    return button;
  }));
}

function initialOf(value: string): string {
  return value.trim().charAt(0).toLocaleUpperCase('tr') || 'A';
}

/**
 * Ücretsiz paketin günlük süresi. Eskiden "Bugün 10:00" yazıyordu ve saat
 * sanılıyordu — ürünün en önemli ticari sınırı görünmez kalıyordu. Artık
 * birimi yazıyor ve yanında ne kadar kaldığını gösteren bir halka var.
 */
export function formatQuotaLeft(seconds: number): string {
  const left = Math.max(0, Math.round(seconds));
  if (left <= 0) return t('quota.exhausted');
  if (left < 60) return t('quota.secondsLeft', { n: left });
  return t('quota.minutesLeft', { n: Math.floor(left / 60) });
}

function quotaIndicator(remaining: number, limit: number): HTMLElement {
  const ratio = limit > 0 ? Math.max(0, Math.min(1, remaining / limit)) : 0;
  const low = remaining < limit * 0.25;
  const ring = el('span', { class: 'quota-ring', 'aria-hidden': 'true' });
  ring.style.setProperty('--p', `${Math.round(ratio * 360)}deg`);
  return el('span', {
    class: 'quota-tag' + (low ? ' low' : ''),
    title: t('quota.title', { n: Math.round(limit / 60) }),
  }, ring, el('span', {}, formatQuotaLeft(remaining)));
}

// --- Hesap menüsü -------------------------------------------------------------

let menuNode: HTMLElement | null = null;

export function closeAccountMenu(): void {
  if (menuNode) { menuNode.remove(); menuNode = null; }
  document.querySelector('.app-nav .avatar')?.setAttribute('aria-expanded', 'false');
  document.removeEventListener('pointerdown', onOutside);
  document.removeEventListener('keydown', onEsc);
}

function onOutside(event: PointerEvent): void {
  if (menuNode && !menuNode.contains(event.target as Node)) closeAccountMenu();
}
function onEsc(event: KeyboardEvent): void {
  if (event.key === 'Escape') closeAccountMenu();
}

function toggleAccountMenu(): void {
  if (menuNode) { closeAccountMenu(); return; }
  const session = state.session;
  const entry = (label: string, name: IconName, action: () => void) => {
    const button = el('button', { type: 'button' }, iconEl(name), el('span', {}, label));
    onTap(button, () => { closeAccountMenu(); action(); });
    return button;
  };
  menuNode = el('div', { class: 'account-menu', role: 'menu' },
    el('div', { class: 'who' },
      el('b', {}, session?.user.displayName || t('nav.myAccount')),
      el('span', {}, session?.user.email ?? ''),
    ),
    entry(t('nav.plans'), 'card', () => navigate('/plans')),
    entry(t('nav.settings'), 'settings', () => navigate('/settings')),
    session?.isAdmin ? entry(t('nav.admin'), 'chart', () => navigate('/admin')) : null,
    entry(t('nav.logout'), 'logout', () => void logout()),
  );
  document.body.appendChild(menuNode);
  document.querySelector('.app-nav .avatar')?.setAttribute('aria-expanded', 'true');
  // Menü, hesap düğmesinin gerçek konumuna yaslanır: geniş ekranda içerik
  // ortalandığı için sabit bir sağ boşluk yanlış yere düşüyordu.
  const anchor = document.querySelector('.app-nav .avatar') as HTMLElement | null;
  if (anchor && window.innerWidth > 700) {
    const box = anchor.getBoundingClientRect();
    menuNode.style.top = `${Math.round(box.bottom + 10)}px`;
    menuNode.style.right = `${Math.round(window.innerWidth - box.right)}px`;
  } else {
    menuNode.style.top = '60px';
  }
  setTimeout(() => {
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('keydown', onEsc);
  }, 0);
  (menuNode.querySelector('button') as HTMLElement | null)?.focus();
}

export function setSearchValue(value: string): void {
  if (searchInput) searchInput.value = value;
}

export async function logout(): Promise<void> {
  try { await api.post('/api/auth/logout'); } catch { /* çerez yine de temizlenir */ }
  location.assign('/');
}

/**
 * Sayfa başlığı + sağdaki eylem.
 * Telefonda üst çubukta arama alanına yer yok (wordmark + kota + hesap zaten
 * dolduruyor), bu yüzden arama sayfa başlığının yanında durur — ama YALNIZCA
 * isteyen ekranda (`options.search`). Ödeme, Paketler ve Uyumluluk ekranlarında
 * genel arama hem yer yiyor hem odak dağıtıyordu.
 */
export function pageIntro(
  title: string,
  right?: Node | null,
  sub?: string,
  options: { search?: boolean } = {},
): HTMLElement {
  const compact = el('div', { class: 'page-search' }, iconEl('search'),
    el('input', {
      type: 'search', placeholder: t('nav.searchPlaceholder'), 'aria-label': t('nav.search'),
      autocomplete: 'off', enterkeyhint: 'search',
    }));
  const input = compact.querySelector('input') as HTMLInputElement;
  compact.appendChild(spaceKey(input));
  input.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key !== 'Enter') return;
    const value = input.value.trim();
    if (value) navigate(`/search?q=${encodeURIComponent(value)}`);
  });
  return el('div', { class: 'page-intro' },
    el('div', {}, el('h1', {}, title), sub ? el('div', { class: 'sub' }, sub) : null),
    right ?? (options.search ? compact : null),
  );
}

export function shelfHead(title: string, actionLabel?: string, onAction?: () => void): HTMLElement {
  const head = el('div', { class: 'shelf-head' }, el('h2', {}, title));
  if (actionLabel && onAction) {
    const button = el('button', { type: 'button' }, actionLabel);
    onTap(button, onAction);
    head.appendChild(button);
  }
  return head;
}
